/**
 * Documents API client — spec-07 W5
 *
 * Base URLs:
 *   /api/v1/documents  → documents-service :8225
 *   /api/v1/tags       → documents-service :8225
 *   /api/v1/versions   → documents-service :8225
 */

import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const DOCS_BASE = '/api/v1/documents';
const TAGS_BASE = '/api/v1/tags';
const VERS_BASE = '/api/v1/versions';

const TENANT_ID = '11111111-1111-1111-1111-111111111111';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DocTag {
  id: string;
  tenant_id: string;
  name: string;
  category: 'type' | 'lifecycle';
  display_name: string | null;
  color_hex: string | null;
  created_at: string;
}

export interface LinkedEntity {
  type: string;
  id: string;
  service: string;
}

export interface DocDocument {
  id: string;
  tenant_id: string;
  title: string;
  document_type: string | null;
  status: string | null;
  owner_user_id: string | null;
  created_at: string;
  updated_at: string;
  expiry_date: string | null;
  description: string | null;
  source_type: string | null;
  metadata: {
    linked_entities?: LinkedEntity[];
    size_bytes?: number;
    [key: string]: unknown;
  } | null;
  tags?: DocTag[];
  // spec-44 W1 columns
  renewal_deadline: string | null;
  responsible_user_id: string | null;
  required_action: string | null;
  subject_type: string | null;
  subject_id: string | null;
  client_company_id: string | null;
}

// ─── Extraction field types (spec-44 W2/W3/W4) ───────────────────────────────

/** review_status enum values on doc_extraction_field */
export type ExtractionReviewStatus =
  | 'pending_review'
  | 'validated'
  | 'edited'
  | 'rejected'
  | 'superseded';

export interface DocExtractionField {
  id: string;
  document_id: string;
  tenant_id: string;
  field_name: string;
  extracted_value: Record<string, unknown>;
  confidence: number;
  review_status: ExtractionReviewStatus;
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
  edited_value: Record<string, unknown> | null;
  extraction_method: string;
  extracted_at: string;
  created_at: string;
  updated_at: string;
}

export type ExtractionFieldAction = 'approve' | 'edit' | 'reject';

export interface ExtractionFieldDecision {
  field_name: string;
  action: ExtractionFieldAction;
  edited_value?: Record<string, unknown>;
}

export interface ExtractionValidatePayload {
  field_decisions: ExtractionFieldDecision[];
}

export interface ExtractionValidateResponse {
  updated_fields: number;
  doc_document_updates: Record<string, unknown>;
}

export interface DocVersion {
  id: string;
  document_id: string;
  version_number: number;
  storage_path: string;
  uploaded_by: string | null;
  uploaded_at: string;
  change_summary: string | null;
}

export interface DocListFilters {
  tenant_id?: string;
  tag_id?: string;
  linked_to_type?: string;
  linked_to_id?: string;
  q?: string;
  skip?: number;
  limit?: number;
}

export interface LinkPayload {
  type: string;
  id: string;
  service: string;
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const documentsKeys = {
  all: ['documents'] as const,
  list: (filters?: DocListFilters) => ['documents', 'list', filters] as const,
  linked: (linkedToType: string, linkedToId: string) =>
    ['documents', 'linked', linkedToType, linkedToId] as const,
  detail: (id: string) => ['documents', 'detail', id] as const,
  tags: ['documents', 'tags'] as const,
  versions: (docId: string) => ['documents', 'versions', docId] as const,
  extractionFields: (docId: string) => ['documents', 'extraction-fields', docId] as const,
};

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchDocuments(filters: DocListFilters = {}): Promise<DocDocument[]> {
  const { data } = await axios.get<DocDocument[]>(`${DOCS_BASE}/`, {
    params: { tenant_id: TENANT_ID, ...filters },
  });
  return data;
}

export async function fetchDocumentsLinked(
  linkedToType: string,
  linkedToId: string,
): Promise<DocDocument[]> {
  const { data } = await axios.get<DocDocument[]>(`${DOCS_BASE}/linked`, {
    params: { linked_to_type: linkedToType, linked_to_id: linkedToId, tenant_id: TENANT_ID },
  });
  return data;
}

export async function fetchDocument(id: string): Promise<DocDocument> {
  const { data } = await axios.get<DocDocument>(`${DOCS_BASE}/${id}`);
  return data;
}

export async function linkDocument(id: string, payload: LinkPayload): Promise<DocDocument> {
  const { data } = await axios.post<DocDocument>(`${DOCS_BASE}/${id}/link`, payload);
  return data;
}

export async function unlinkDocument(id: string, payload: LinkPayload): Promise<DocDocument> {
  const { data } = await axios.post<DocDocument>(`${DOCS_BASE}/${id}/unlink`, payload);
  return data;
}

export async function tagDocument(id: string, tagId: string): Promise<DocDocument> {
  const { data } = await axios.post<DocDocument>(`${DOCS_BASE}/${id}/tag`, { tag_id: tagId });
  return data;
}

export async function untagDocument(id: string, tagId: string): Promise<DocDocument> {
  const { data } = await axios.delete<DocDocument>(`${DOCS_BASE}/${id}/tag/${tagId}`);
  return data;
}

export async function fetchTags(): Promise<DocTag[]> {
  const { data } = await axios.get<DocTag[]>(`${TAGS_BASE}/`, {
    params: { tenant_id: TENANT_ID },
    headers: { 'X-Tenant-ID': TENANT_ID },
  });
  return data;
}

export async function fetchVersions(documentId: string): Promise<DocVersion[]> {
  const { data } = await axios.get<DocVersion[]>(`${VERS_BASE}/`, {
    params: { document_id: documentId },
  });
  return data;
}

export async function fetchExtractionFields(documentId: string): Promise<DocExtractionField[]> {
  const { data } = await axios.get<DocExtractionField[]>(
    `${DOCS_BASE}/${documentId}/extraction-fields`,
    { params: { status: 'all' } },
  );
  return data;
}

export async function validateExtraction(
  documentId: string,
  payload: ExtractionValidatePayload,
): Promise<ExtractionValidateResponse> {
  const { data } = await axios.post<ExtractionValidateResponse>(
    `${DOCS_BASE}/${documentId}/extraction-validate`,
    payload,
  );
  return data;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useDocuments(filters?: DocListFilters) {
  return useQuery({
    queryKey: documentsKeys.list(filters),
    queryFn: () => fetchDocuments(filters),
    staleTime: 10_000,
  });
}

export function useDocumentsLinked(linkedToType: string, linkedToId: string) {
  return useQuery({
    queryKey: documentsKeys.linked(linkedToType, linkedToId),
    queryFn: () => fetchDocumentsLinked(linkedToType, linkedToId),
    enabled: !!(linkedToType && linkedToId),
    staleTime: 10_000,
  });
}

export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: documentsKeys.detail(id ?? ''),
    queryFn: () => fetchDocument(id!),
    enabled: !!id,
  });
}

export function useTags() {
  return useQuery({
    queryKey: documentsKeys.tags,
    queryFn: fetchTags,
    staleTime: 60_000,
  });
}

export function useDocumentVersions(documentId: string | undefined) {
  return useQuery({
    queryKey: documentsKeys.versions(documentId ?? ''),
    queryFn: () => fetchVersions(documentId!),
    enabled: !!documentId,
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => {
      return axios.post<DocDocument>(`${DOCS_BASE}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentsKeys.all });
    },
  });
}

export function useLinkDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: LinkPayload }) =>
      linkDocument(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentsKeys.all });
    },
  });
}

export function useUnlinkDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: LinkPayload }) =>
      unlinkDocument(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentsKeys.all });
    },
  });
}

export function useTagDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tagId }: { id: string; tagId: string }) => tagDocument(id, tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentsKeys.all });
    },
  });
}

export function useExtractionFields(documentId: string | undefined) {
  return useQuery({
    queryKey: documentsKeys.extractionFields(documentId ?? ''),
    queryFn: () => fetchExtractionFields(documentId!),
    enabled: !!documentId,
    staleTime: 15_000,
  });
}

export function useValidateExtraction(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ExtractionValidatePayload) =>
      validateExtraction(documentId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentsKeys.detail(documentId) });
      qc.invalidateQueries({ queryKey: documentsKeys.extractionFields(documentId) });
    },
  });
}

export function useUntagDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tagId }: { id: string; tagId: string }) => untagDocument(id, tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentsKeys.all });
    },
  });
}
